import { Component, EventEmitter, Input, Output, ViewChild, ElementRef } from '@angular/core';
import { CustomerService } from '../../services/customer.service';
import { CommonService } from '../../services/common.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-upload-popup-image',
  templateUrl: './upload-image.component.html',
  styleUrls: ['./upload-image.component.scss']
})
export class UploadImageComponent {
  @Input() filter: string = '';
  @Input() type: string = '';
  @Input() accept: string = 'image/*';
  @Input() maxSize: number = 10 * 1024 * 1024; // 10MB default
  @Input() showPreview: boolean = true;
  @Input() label: string = '';
  @Input() placeholder: string = 'Upload popup image';

  @Output() uploadSuccess = new EventEmitter<any>();
  @Output() uploadError = new EventEmitter<any>();
  @Output() imageSelected = new EventEmitter<File>();

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  selectedFile: File | null = null;
  imagePreview: string | null = null;
  isUploading: boolean = false;
  uploadProgress: number = 0;

  constructor(
    private customerService: CustomerService,
    private commonService: CommonService,
    private toastr: ToastrService
  ) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Validate file size
      if (file.size > this.maxSize) {
        this.toastr.error(`File size exceeds ${this.maxSize / (1024 * 1024)}MB limit`);
        this.resetFileInput();
        return;
      }

      // Validate file type
      if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/i)) {
        this.toastr.error('Please select a valid image file (JPG, PNG, GIF, or WEBP)');
        this.resetFileInput();
        return;
      }

      this.selectedFile = file;
      this.imageSelected.emit(file);

      // Create preview
      if (this.showPreview) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    }
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement.click();
  }

  removeImage(): void {
    this.selectedFile = null;
    this.imagePreview = null;
    this.resetFileInput();
  }

  uploadImage(): void {
    if (!this.selectedFile) {
      this.toastr.warning('Please select an image first');
      return;
    }

    if (!this.filter || !this.type) {
      this.toastr.error('Filter and Type are required');
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;

    const formData = new FormData();
    formData.append('image', this.selectedFile);
    formData.append('filter', this.filter);
    formData.append('type', this.type);

    this.customerService.addPopUpImage(formData).subscribe(
      (response: any) => {
        this.isUploading = false;
        this.uploadProgress = 100;
        this.toastr.success('Image uploaded successfully');
        // Refresh popup images observer
        this.commonService.getPopupImages(this.filter);
        this.uploadSuccess.emit(response);
        this.resetAfterUpload();
      },
      (error: any) => {
        this.isUploading = false;
        this.uploadProgress = 0;
        const errorMessage = error?.error?.message || 'Failed to upload image';
        this.toastr.error(errorMessage);
        this.uploadError.emit(error);
      }
    );
  }

  private resetAfterUpload(): void {
    this.selectedFile = null;
    this.imagePreview = null;
    this.resetFileInput();
    setTimeout(() => {
      this.uploadProgress = 0;
    }, 1000);
  }

  private resetFileInput(): void {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  get hasImage(): boolean {
    return !!this.selectedFile;
  }

  get canUpload(): boolean {
    // Allow upload if file is selected and not currently uploading
    // Filter and type are validated in uploadImage() method
    return !!this.selectedFile && !this.isUploading;
  }

  getFileSize(bytes: number | undefined): string {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }
}

